import { Bookmark, BookmarkCheck, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useSavedFilters,
  useUpdateSavedFilter,
} from '@/features/saved-filters/queries';
import type {
  SavedFilter,
  SavedFilterScope,
} from '@/features/saved-filters/types';

/**
 * L16 — saved filter menu rendered as a Bookmark dropdown.
 *
 * Behaviour:
 *   - Lists the actor's filters for `scope`.
 *   - "Save as…" captures the current `currentFilter` blob.
 *   - "Set as default" is a per-row toggle.
 *   - Clicking a filter calls `onApply(filter)` so the host page
 *     replaces its URL params with the filter's blob.
 *
 * Empty state: shows a "Save current filter" CTA so first-time users
 * understand what the menu is for.
 */
export function SavedFilterMenu({
  scope,
  currentFilter,
  onApply,
}: {
  scope: SavedFilterScope;
  /** Current filter values from the host page — used by "Save as…". */
  currentFilter: Record<string, unknown>;
  /** Callback invoked when the user picks a saved filter. */
  onApply: (filter: SavedFilter) => void;
}) {
  const { t } = useTranslation();
  const { data: filters, isLoading } = useSavedFilters(scope);
  const createMut = useCreateSavedFilter();
  const updateMut = useUpdateSavedFilter(scope);
  const deleteMut = useDeleteSavedFilter(scope);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const onSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createMut.mutate(
      { scope, name: trimmed, filterJson: currentFilter },
      {
        onSuccess: () => {
          setSaveOpen(false);
          setName('');
        },
      },
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="saved-filter-menu-trigger"
          >
            <Bookmark aria-hidden className="mr-1 h-4 w-4" />
            {t('bulk.savedFilters.menu')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {isLoading && (
            <DropdownMenuItem disabled>
              {t('common.loading')}
            </DropdownMenuItem>
          )}
          {!isLoading && (filters?.length ?? 0) === 0 && (
            <DropdownMenuItem disabled className="text-xs">
              {t('bulk.savedFilters.empty')}
            </DropdownMenuItem>
          )}
          {(filters ?? []).map((f) => (
            <DropdownMenuItem
              key={f.id}
              onSelect={() => onApply(f)}
              className="flex items-center justify-between gap-2"
              data-testid={`saved-filter-item-${f.id}`}
            >
              <span className="flex flex-1 items-center gap-2 truncate">
                {f.isDefault ? (
                  <BookmarkCheck
                    aria-hidden
                    className="h-3.5 w-3.5 text-primary"
                  />
                ) : (
                  <Bookmark aria-hidden className="h-3.5 w-3.5" />
                )}
                <span className="truncate">{f.name}</span>
              </span>
              <span className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={t('bulk.savedFilters.setDefault')}
                  data-testid={`saved-filter-default-${f.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateMut.mutate({
                      id: f.id,
                      patch: { isDefault: !f.isDefault },
                    });
                  }}
                >
                  <Star
                    aria-hidden
                    className={`h-3.5 w-3.5 ${
                      f.isDefault ? 'fill-current text-primary' : ''
                    }`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  aria-label={t('bulk.savedFilters.delete')}
                  data-testid={`saved-filter-delete-${f.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteMut.mutate(f.id);
                  }}
                >
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </Button>
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setSaveOpen(true);
            }}
            data-testid="saved-filter-save-as"
          >
            <Bookmark aria-hidden className="mr-2 h-4 w-4" />
            {t('bulk.savedFilters.saveAs')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulk.savedFilters.saveModalTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="saved-filter-name">
              {t('bulk.savedFilters.nameLabel')}
            </Label>
            <Input
              id="saved-filter-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              data-testid="saved-filter-name-input"
              placeholder={t('bulk.savedFilters.namePlaceholder')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={onSave}
              disabled={!name.trim() || createMut.isPending}
              data-testid="saved-filter-save-confirm"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
